import { useEffect } from 'react';

import {
  WS_EVENTS,
  type AiConductingQueueHeldPayload,
  type EscalationConfirmedPayload,
  type EscalationDeclinedOrTimeoutPayload,
  type EscalationSuggestedPayload,
} from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

/**
 * useEscalationEvents — Items 280+281 (T B10).
 *
 * Assina os eventos WS de escalation consent no modo H_c + (S7 PR2) o
 * `queue-held` do modo recepcionista (S4), filtrados por `conversationId`.
 * Cada evento recebido é forwarded para `onEvent` com discriminador de tipo
 * (EscalationEvent union).
 *
 * Padrão WS canônico: envelope.data (não .payload) — conforme
 * WebSocketProvider.tsx e use-conversation-events.ts.
 */

export type EscalationEvent =
  | { type: 'suggested'; payload: EscalationSuggestedPayload }
  | { type: 'confirmed'; payload: EscalationConfirmedPayload }
  | { type: 'declined-or-timeout'; payload: EscalationDeclinedOrTimeoutPayload }
  // S7 PR2 — a IA passou a SEGURAR a fila (modo recepcionista S4, gated).
  | { type: 'queue-held'; payload: AiConductingQueueHeldPayload };

export function useEscalationEvents(
  conversationId: string,
  onEvent: (event: EscalationEvent) => void,
): void {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const unsubSuggested = subscribe<EscalationSuggestedPayload>(
      WS_EVENTS.AI_CONDUCTING_ESCALATION_SUGGESTED,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        onEvent({ type: 'suggested', payload: envelope.data });
      },
    );

    const unsubConfirmed = subscribe<EscalationConfirmedPayload>(
      WS_EVENTS.AI_CONDUCTING_ESCALATION_CONFIRMED,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        onEvent({ type: 'confirmed', payload: envelope.data });
      },
    );

    const unsubDeclinedOrTimeout = subscribe<EscalationDeclinedOrTimeoutPayload>(
      WS_EVENTS.AI_CONDUCTING_ESCALATION_DECLINED_OR_TIMEOUT,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        onEvent({ type: 'declined-or-timeout', payload: envelope.data });
      },
    );

    const unsubQueueHeld = subscribe<AiConductingQueueHeldPayload>(
      WS_EVENTS.AI_CONDUCTING_QUEUE_HELD,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        onEvent({ type: 'queue-held', payload: envelope.data });
      },
    );

    return () => {
      unsubSuggested();
      unsubConfirmed();
      unsubDeclinedOrTimeout();
      unsubQueueHeld();
    };
    // onEvent is excluded from deps intentionally — callers should memoize
    // or accept re-subscribe on identity change. conversationId triggers full re-subscribe.
  }, [conversationId, subscribe]);
}
